import React, { useCallback, useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Divider,
	Grid,
	FormControlLabel,
	FormGroup,
	Stack,
	Switch,
	TextField,
	Typography,
	Tabs,
	Tab,
	Skeleton,
	IconButton,
	Tooltip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SaveIcon from "@mui/icons-material/Save";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
	insertOptions,
	uploadCompanyLogo,
	getBrandingLogos,
	selectBrandingLogo,
	uploadCompanyBackdrop,
	getBrandingBackdrops,
	deleteBrandingAsset,
	getBrandingStockImages,
	uploadCompanyStockImage,
} from "../../services/defaultValuesService";

import { useOptions } from "../../context/optionContext";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { ASSETS_BASE_URL, COMPANY_EMAIL, COMPANY_NAME, COMPANY_PHONE } from "../../config";
import ModalWrapper from "../ModalWrapper/ModalWrapper";

const normalizePath = (value, fallback, { allowEmpty = false } = {}) => {
	if (allowEmpty && (value === "" || value === null)) return "";
	const raw = typeof value === "string" && value.trim().length ? value.trim() : fallback;
	if (!raw) return fallback;
	return raw.startsWith("/") ? raw : `/${raw}`;
};

const isEnabled = (value, defaultValue = true) => {
	if (value === undefined || value === null) return defaultValue;
	if (typeof value === "boolean") return value;
	return value !== "false" && value !== "0";
};

const CompanyBranding = () => {
	const { t } = useTranslation();
	const { optionKeys, fetchOptions } = useOptions();
	const {
		primaryLogoPath,
		secondaryLogoPath,
		footerPrimaryLogoPath,
		iconPath,
	} = useBrandingAssets();

	const AUTH_BACKDROP_USAGE_OPTIONS = [
		{ key: "authBackdropLoginEnabled", label: t('BUTTON.LOGIN') },
		{ key: "authBackdropRegisterEnabled", label: t('BUTTON.REGISTER') },
		{ key: "authBackdropForgotPasswordEnabled", label: t('FORMS.FORGOT_PASSWORD_1') },
		{ key: "authBackdropResetPasswordEnabled", label: t('BUTTON.RESET_PASSWORD') },
		{ key: "authBackdropVerifyEmailEnabled", label: t('TITLES.VERIFY_EMAIL') },
		{ key: "authBackdropAcceptEulaEnabled", label: t('EULA_ADMIN.ACCEPT_EULA_1') },
		{ key: "authBackdropLogoutEnabled", label: t('BUTTON.LOGOUT') },
		{ key: "authBackdropTrainingEnabled", label: t('LABELS.TRAINING') },
		{ key: "authBackdropPVMDashboardEnabled", label: t('UI_TEXT.PVM_DASHBOARD') },
		{ key: "authBackdropPMDashboardEnabled", label: t('UI_TEXT.PM_DASHBOARD') },
		{ key: "authBackdropSOSDashboardEnabled", label: t('UI_TEXT.SOS_DASHBOARD') },
		{ key: "authBackdropMachineDashboardEnabled", label: t('TITLES.MACHINE_DASHBOARD') },
	];

	const [activeTab, setActiveTab] = useState(0);
	const [logoList, setLogoList] = useState([]);
	const [logoLoading, setLogoLoading] = useState(false);
	const [backdropList, setBackdropList] = useState([]);
	const [backdropLoading, setBackdropLoading] = useState(false);
	const [stockList, setStockList] = useState([]);
	const [stockLoading, setStockLoading] = useState(false);

	const [uploading, setUploading] = useState(false);
	const [backdropUploading, setBackdropUploading] = useState(false);
	const [stockUploading, setStockUploading] = useState(false);
	const [savingInfo, setSavingInfo] = useState(false);

	const [selectedLogo, setSelectedLogo] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedBackdrop, setSelectedBackdrop] = useState(null);
	const [backdropModalOpen, setBackdropModalOpen] = useState(false);
	const [stockModalOpen, setStockModalOpen] = useState(false);
	const [iconError, setIconError] = useState(""); // ❗ For 1:1 validation error

	const [companyInfo, setCompanyInfo] = useState({
		companyName: "",
		companyEmail: "",
		companyPhone: "",
		emailFromUseCompanyName: true,
		emailFromName: "",
		companyWebsite: "",
		companyAddress: "",
		companySocial1: "",
		companySocial2: "",
		companySocial3: "",
		companySocial4: "",
	});

	const [backdropState, setBackdropState] = useState({
		authBackdropPath: "/home_bg2.jpg",
		homeBackdropPath: "/home_bg3.jpg",
	});
	const [stockState, setStockState] = useState({
		stockImagePath: "/stock.png",
	});
	const [backdropUsage, setBackdropUsage] = useState({
		homeBackdropEnabled: true,
		...AUTH_BACKDROP_USAGE_OPTIONS.reduce((acc, item) => ({ ...acc, [item.key]: true }), {}),
	});

	// Load option values
	useEffect(() => {
		if (!optionKeys) return;

		setCompanyInfo({
			companyName: optionKeys?.companyName || COMPANY_NAME || "",
			companyEmail: optionKeys?.companyEmail || COMPANY_EMAIL || "",
			companyPhone: optionKeys?.companyPhone || COMPANY_PHONE || "",
			emailFromUseCompanyName: isEnabled(optionKeys?.emailFromUseCompanyName, true),
			emailFromName: optionKeys?.emailFromName || "",
			companyWebsite: optionKeys?.companyWebsite || "",
			companyAddress: optionKeys?.companyAddress || "",
			companySocial1: optionKeys?.companySocial1 || "",
			companySocial2: optionKeys?.companySocial2 || "",
			companySocial3: optionKeys?.companySocial3 || "",
			companySocial4: optionKeys?.companySocial4 || "",
		});

		setBackdropState({
			authBackdropPath: normalizePath(optionKeys?.authBackdropPath, "/home_bg2.jpg", { allowEmpty: true }),
			homeBackdropPath: normalizePath(optionKeys?.homeBackdropPath, "/home_bg3.jpg", { allowEmpty: true }),
		});
		setStockState({
			stockImagePath: normalizePath(optionKeys?.stockImagePath, "/stock.png"),
		});
		setBackdropUsage({
			homeBackdropEnabled: isEnabled(optionKeys?.homeBackdropEnabled, true),
			...AUTH_BACKDROP_USAGE_OPTIONS.reduce((acc, item) => {
				acc[item.key] = isEnabled(optionKeys?.[item.key], true);
				return acc;
			}, {}),
		});
	}, [optionKeys]);

	const loadBrandingFiles = useCallback(async () => {
		setLogoLoading(true);
		try {
			const resp = await getBrandingLogos();
			const files = resp?.data?.files || resp?.files || [];
			setLogoList(files);
		} catch (err) {
			setLogoList([]);
		}
		setLogoLoading(false);
	}, []);

	const loadBackdropFiles = useCallback(async () => {
		setBackdropLoading(true);
		try {
			const resp = await getBrandingBackdrops();
			const files = resp?.data?.files || resp?.files || [];
			setBackdropList(files);
		} catch (err) {
			setBackdropList([]);
		}
		setBackdropLoading(false);
	}, []);

	const loadStockFiles = useCallback(async () => {
		setStockLoading(true);
		try {
			const resp = await getBrandingStockImages();
			const files = resp?.data?.files || resp?.files || [];
			setStockList(files);
		} catch (err) {
			setStockList([]);
		}
		setStockLoading(false);
	}, []);

	useEffect(() => {
		loadBrandingFiles();
	}, [loadBrandingFiles]);

	useEffect(() => {
		loadBackdropFiles();
	}, [loadBackdropFiles]);

	useEffect(() => {
		loadStockFiles();
	}, [loadStockFiles]);

	// Save Company Info
	const saveCompanyInfo = async () => {
		setSavingInfo(true);

		const payload = [
			{ option_key: "companyName", option_value: companyInfo.companyName },
			{ option_key: "companyEmail", option_value: companyInfo.companyEmail },
			{ option_key: "companyPhone", option_value: companyInfo.companyPhone },
			{ option_key: "emailFromUseCompanyName", option_value: String(companyInfo.emailFromUseCompanyName) },
			{ option_key: "emailFromName", option_value: companyInfo.emailFromName },
			{ option_key: "companyWebsite", option_value: companyInfo.companyWebsite },
			{ option_key: "companyAddress", option_value: companyInfo.companyAddress },
			{ option_key: "companySocial1", option_value: companyInfo.companySocial1 },
			{ option_key: "companySocial2", option_value: companyInfo.companySocial2 },
			{ option_key: "companySocial3", option_value: companyInfo.companySocial3 },
			{ option_key: "companySocial4", option_value: companyInfo.companySocial4 },
		];

		try {
			const resp = await insertOptions({ options: payload });
			if (!resp?.status) throw new Error(resp?.message);

			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: resp?.message || t("UI_TEXT.COMPANY_INFO_SAVED", { defaultValue: "Saved successfully" })
			});

			await fetchOptions();
		} catch (err) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: err?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Failed to save company information" })
			});
		}

		setSavingInfo(false);
	};

	// Upload Logo Manually
	const handleLogoUpload = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";

		if (!file) return;

		setUploading(true);

		try {
			const formData = new FormData();
			formData.append("file", file);

			const resp = await uploadCompanyLogo(formData);
			if (!resp?.status) throw new Error(resp?.message);

			await loadBrandingFiles();
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("UI_TEXT.LOGO_UPLOADED", { defaultValue: "Logo uploaded successfully" }),
			});
		} catch (err) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: err.message,
			});
		}

		setUploading(false);
	};

	const handleBackdropUpload = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file) return;
		setBackdropUploading(true);
		try {
			const formData = new FormData();
			formData.append("file", file);
			const resp = await uploadCompanyBackdrop(formData);
			if (!resp?.status) throw new Error(resp?.message);
			await loadBackdropFiles();
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("UI_TEXT.BACKDROP_UPLOADED", { defaultValue: "Backdrop uploaded successfully" }),
			});
		} catch (err) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: err.message,
			});
		}
		setBackdropUploading(false);
	};

	const handleStockUpload = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file) return;
		setStockUploading(true);
		try {
			const formData = new FormData();
			formData.append("file", file);
			const resp = await uploadCompanyStockImage(formData);
			if (!resp?.status) throw new Error(resp?.message);
			await loadStockFiles();
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("UI_TEXT.STOCK_IMAGE_UPLOADED", { defaultValue: "Stock image uploaded successfully" }),
			});
		} catch (err) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: err.message,
			});
		}
		setStockUploading(false);
	};

	const handleAssetDelete = async (assetPath, typeLabel = "logo") => {
		if (!assetPath || !assetPath.startsWith("/branding/")) return;

		const confirmResult = await Swal.fire({
			title: t("TITLES.DELETE_FILE", { defaultValue: "Delete file?" }),
			text: t("UI_TEXT.DELETE_ASSET_CONFIRMATION", {
				defaultValue: "This will permanently delete the selected file.",
			}),
			icon: "warning",
			showCancelButton: true,
			confirmButtonColor: "#d33",
			confirmButtonText: t("BUTTON.DELETE", { defaultValue: "Delete" }),
		});

		if (!confirmResult.isConfirmed) return;

		try {
			const resp = await deleteBrandingAsset(assetPath);
			if (!resp?.status) throw new Error(resp?.message);
			if (typeLabel === "backdrop") {
				await loadBackdropFiles();
			} else if (typeLabel === "stock") {
				await loadStockFiles();
			} else {
				await loadBrandingFiles();
			}
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.DELETED", { defaultValue: "Deleted" }),
				text: t("UI_TEXT.ASSET_DELETED", { defaultValue: "File deleted successfully." }),
			});
		} catch (err) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: err?.message || t("UI_TEXT.ERROR_DELETING_ASSET", { defaultValue: "Failed to delete file." }),
			});
		}
	};

	// Persist logo to selected type
	const persistLogoSelection = async (type) => {
		if (!selectedLogo) return;

		if (type === "icon") {
			setIconError(""); // clear old errors

			const imgUrl = `${ASSETS_BASE_URL}${selectedLogo.path}`;
			const image = new Image();
			image.src = imgUrl;

			await new Promise((resolve) => (image.onload = resolve));

			if (image.width !== image.height) {
				setIconError(
					t("UI_TEXT.ERROR_ICON_RATIO", {
						defaultValue: "For Browser Icon, image must have 1:1 square ratio."
					})
				);
				return;
			}
		}

		const map = {
			primary: "primaryLogoPath",
			secondary: "secondaryLogoPath",
			footerPrimary: "footerPrimaryLogoPath",
			icon: "siteIconPath",
		};

		const key = map[type];

		try {
			const selectedResp = await selectBrandingLogo(type, selectedLogo.path);
			const selectedPath = selectedResp?.data?.path;
			if (!selectedResp?.status || !selectedPath) {
				throw new Error(selectedResp?.message || "Unable to prepare selected logo");
			}

			const resp = await insertOptions({
				options: [{ option_key: key, option_value: selectedPath }],
			});

			if (!resp?.status) throw new Error(resp?.message);

			await fetchOptions();

			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("UI_TEXT.LOGO_UPDATED", { defaultValue: "Logo updated" }),
			});

			setModalOpen(false);
		} catch (err) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: err.message,
			});
		}
	};

	const persistBackdropSelection = async (type, overridePath) => {
		const map = {
			auth: "authBackdropPath",
			home: "homeBackdropPath",
		};
		const key = map[type];
		if (!key) return;
		const pathToSave = overridePath !== undefined ? overridePath : selectedBackdrop?.path;
		if (pathToSave === undefined) return;

		try {
			const resp = await insertOptions({
				options: [{ option_key: key, option_value: pathToSave }],
			});

			if (!resp?.status) throw new Error(resp?.message);
			setBackdropState((prev) => ({ ...prev, [key]: pathToSave || "" }));
			await fetchOptions();
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("LABELS.BACKDROP_UPDATED_SUCCESSFULLY", { defaultValue: "Backdrop updated successfully" }),
			});
			setBackdropModalOpen(false);
		} catch (err) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: err.message,
			});
		}
	};

	const persistStockSelection = async (pathToSave) => {
		if (!pathToSave) return;
		try {
			const resp = await insertOptions({
				options: [{ option_key: "stockImagePath", option_value: pathToSave }],
			});
			if (!resp?.status) throw new Error(resp?.message);
			setStockState({ stockImagePath: pathToSave });
			await fetchOptions();
			Swal.fire({
				icon: "success",
				title: t("ALERT_MESSAGES.SUCCESS", { defaultValue: "Success" }),
				text: t("UI_TEXT.STOCK_IMAGE_UPDATED", { defaultValue: "Stock image updated successfully" }),
			});
			setStockModalOpen(false);
		} catch (err) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: err.message,
			});
		}
	};

	const updateBackdropUsage = async (optionKey, enabled) => {
		setBackdropUsage((prev) => ({ ...prev, [optionKey]: enabled }));
		try {
			const resp = await insertOptions({
				options: [{ option_key: optionKey, option_value: String(enabled) }],
			});
			if (!resp?.status) throw new Error(resp?.message);
			await fetchOptions();
		} catch (err) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR", { defaultValue: "Error" }),
				text: err?.message || t("UI_TEXT.ERROR_SAVING", { defaultValue: "Failed to save changes" }),
			});
		}
	};

	return (
		<>
			<Box sx={{ width: "100%" }}>
				<Tabs
					value={activeTab}
					onChange={(_, v) => setActiveTab(v)}
					variant="scrollable"
					scrollButtons="auto"
					sx={{ borderBottom: "1px solid #ddd", mb: 3 }}
				>
					<Tab label={t("LABELS.BASIC_INFORMATION", { defaultValue: "Basic Company Information" })} />
					<Tab label={t("LABELS.LOGOS", { defaultValue: "Branding & Logos" })} />
					<Tab label={t("LABELS.BACKDROPS", { defaultValue: "Backdrops" })} />
					<Tab label={t("LABELS.STOCK_IMAGE", { defaultValue: "Stock Image" })} />


				</Tabs>

				{/* TAB 1 - Company Info */}
				<Box hidden={activeTab !== 0}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

						<TextField
							label={t('LABELS.COMPANY_NAME')}
							value={companyInfo.companyName}
							onChange={(e) =>
								setCompanyInfo((p) => ({ ...p, companyName: e.target.value }))
							}
							size="small"
							fullWidth
						/>

						<TextField
							label={t('UI_TEXT.COMPANY_EMAIL')}
							value={companyInfo.companyEmail}
							onChange={(e) =>
								setCompanyInfo((p) => ({ ...p, companyEmail: e.target.value }))
							}
							size="small"
							fullWidth
						/>

						<TextField
							label={t('UI_TEXT.COMPANY_WEBSITE')}
							value={companyInfo.companyWebsite}
							onChange={(e) =>
								setCompanyInfo((p) => ({ ...p, companyWebsite: e.target.value }))
							}
							size="small"
							fullWidth
						/>
						<TextField
							label="Company Phone"
							value={companyInfo.companyPhone}
							onChange={(e) =>
								setCompanyInfo((p) => ({ ...p, companyPhone: e.target.value }))
							}
							size="small"
							fullWidth
						/>
						<FormGroup>
							<FormControlLabel
								control={
									<Switch
										checked={companyInfo.emailFromUseCompanyName}
										onChange={(e) =>
											setCompanyInfo((p) => ({ ...p, emailFromUseCompanyName: e.target.checked }))
										}
									/>
								}
								label="Use Company Name as Email From Name"
							/>
						</FormGroup>
						{!companyInfo.emailFromUseCompanyName && (
							<TextField
								label="Email From Name"
								value={companyInfo.emailFromName}
								onChange={(e) =>
									setCompanyInfo((p) => ({ ...p, emailFromName: e.target.value }))
								}
								size="small"
								fullWidth
							/>
						)}

						<TextField
							label={t('LABELS.COMPANY_ADDRESS')}
							value={companyInfo.companyAddress}
							onChange={(e) =>
								setCompanyInfo((p) => ({ ...p, companyAddress: e.target.value }))
							}
							size="small"
							fullWidth
							multiline
							rows={3}
						/>
						<Divider />
						<Typography variant="subtitle1" fontWeight={600}>
							Social Links
						</Typography>
						<TextField
							label={t('LABELS.FACEBOOK_URL')}
							value={companyInfo.companySocial1}
							onChange={(e) =>
								setCompanyInfo((p) => ({ ...p, companySocial1: e.target.value }))
							}
							size="small"
							fullWidth
						/>
						<TextField
							label={t('LABELS.LINKEDIN_URL')}
							value={companyInfo.companySocial2}
							onChange={(e) =>
								setCompanyInfo((p) => ({ ...p, companySocial2: e.target.value }))
							}
							size="small"
							fullWidth
						/>
						<TextField
							label={t('LABELS.X_TWITTER_URL')}
							value={companyInfo.companySocial3}
							onChange={(e) =>
								setCompanyInfo((p) => ({ ...p, companySocial3: e.target.value }))
							}
							size="small"
							fullWidth
						/>
						<TextField
							label={t('LABELS.YOUTUBE_URL')}
							value={companyInfo.companySocial4}
							onChange={(e) =>
								setCompanyInfo((p) => ({ ...p, companySocial4: e.target.value }))
							}
							size="small"
							fullWidth
						/>

						<Box textAlign="right">
							<Button
								variant="contained"
								startIcon={<SaveIcon />}
								onClick={saveCompanyInfo}
								disabled={savingInfo}
							>
								{savingInfo ? "Saving..." : t("BUTTON.SAVE_CHANGES")}
							</Button>
						</Box>


					</Box>
				</Box>

				{/* TAB 2 — BRANDING & LOGOS */}
				<Box hidden={activeTab !== 1}>
					<Grid container spacing={2}>

						{/* ACTIVE — Primary Logo */}
						<Grid item xs={12} md={3}>
							<Card variant="outlined">
								<CardContent>
									<Typography variant="h6" mb={1.5}>{t("LABELS.PRIMARY_LOGO", { defaultValue: "Primary Logo" })}</Typography>
									<Box
										sx={{
											width: "100%",
											height: 120,
											border: "1px solid #ddd",
											borderRadius: 1,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											overflow: "hidden",
											background: "#fff",
										}}
									>
										<img
							src={`${ASSETS_BASE_URL}${primaryLogoPath}`}
											alt="Primary"
											style={{ maxWidth: "100%", maxHeight: 100 }}
										/>
									</Box>
								</CardContent>
							</Card>
						</Grid>

						{/* ACTIVE — Footer Primary Logo */}
						<Grid item xs={12} md={3}>
							<Card variant="outlined">
								<CardContent>
									<Typography variant="h6" mb={1.5}>{t("LABELS.FOOTER_PRIMARY_LOGO", { defaultValue: "Footer Primary Logo" })}</Typography>
									<Box
										sx={{
											width: "100%",
											height: 120,
											border: "1px solid #ddd",
											borderRadius: 1,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											overflow: "hidden",
											background: "#fff",
										}}
									>
										<img
							src={`${ASSETS_BASE_URL}${footerPrimaryLogoPath}`}
											alt="Footer Primary"
											style={{ maxWidth: "100%", maxHeight: 100 }}
										/>
									</Box>
								</CardContent>
							</Card>
						</Grid>

						{/* ACTIVE — Footer Logo */}
						<Grid item xs={12} md={3}>
							<Card variant="outlined">
								<CardContent>
									<Typography variant="h6" mb={1.5}>{t("LABELS.FOOTER_LOGO", { defaultValue: "Footer Logo" })}</Typography>
									<Box
										sx={{
											width: "100%",
											height: 120,
											border: "1px solid #ddd",
											borderRadius: 1,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											overflow: "hidden",
											background: "#fff",
										}}
									>
										<img
							src={`${ASSETS_BASE_URL}${secondaryLogoPath}`}
											alt={t('COMPANY.FOOTER_1')}
											style={{ maxWidth: "100%", maxHeight: 100 }}
										/>
									</Box>
								</CardContent>
							</Card>
						</Grid>

						{/* ACTIVE — Browser Icon */}
						<Grid item xs={12} md={3}>
							<Card variant="outlined">
								<CardContent>
									<Typography variant="h6" mb={1.5}>{t("LABELS.BROWSER_ICON", { defaultValue: "Browser Icon" })}</Typography>
									<Box
										sx={{
											width: "100%",
											height: 120,
											border: "1px solid #ddd",
											borderRadius: 1,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											overflow: "hidden",
											background: "#fff",
										}}
									>
										<img
							src={`${ASSETS_BASE_URL}${iconPath}`}
											alt={t('COMPANY.ICON')}
											style={{ maxWidth: "100%", maxHeight: 100 }}
										/>
									</Box>
								</CardContent>
							</Card>
						</Grid>

						{/* SECTION: AVAILABLE LOGOS */}
						<Grid item xs={12}>
							<Divider sx={{ mt: 2 }} />
							<Typography variant="h6" mt={2} mb={1}>
								{t("LABELS.AVAILABLE_LOGOS", { defaultValue: "Available Logos" })}
							</Typography>

							{/* Upload new */}
							<Box sx={{ borderRadius: '10px', background: '#eee', p: 1.5, display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%', columnGap: 2, mb: 2 }}>
								<Button
									variant="contained"
									component="label"
									startIcon={<CloudUploadIcon />}
									disabled={uploading}

								>
									{uploading
										? t("BUTTON.UPLOADING", { defaultValue: "Uploading..." })
										: t("LABELS.UPLOAD_LOGO", { defaultValue: "Upload Logo" })}
									<input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
								</Button>
								<Typography variant="body1" fontSize="11px">
									{t('UI_TEXT.UPLOAD_A_PNG_JPG_OR_SVG_LOGO_MAXIMUM_SIZE_5_MB')}
								</Typography>

							</Box>


							{logoLoading ? (
								<Grid container spacing={2}>
									{[...Array(8)].map((_, i) => (
										<Grid item xs={6} sm={4} md={3} lg={2} key={i}>
											<Card variant="outlined" sx={{ p: 1 }}>
												<Skeleton
													variant="rectangular"
													sx={{
														width: "100%",
														height: 120,
														borderRadius: 1,
													}}
												/>

												<Skeleton variant="text" sx={{ mt: 1, width: "80%" }} />

												<Skeleton
													variant="rectangular"
													sx={{ mt: 1, height: 36, borderRadius: 1 }}
												/>
											</Card>
										</Grid>
									))}
								</Grid>
							) : (
								<Grid container spacing={2}>
									{logoList.map((logo) => {
						const logoBaseName = logo.path?.split("/").pop()?.replace(/\.[^.]+$/, "");
						const protectedLogo = ["primaryLogo", "footerPrimaryLogo", "footerSecondaryLogo", "primaryFavicon"].includes(logoBaseName);
						const removable = logo.path?.startsWith("/branding/") && !protectedLogo;
										return (
											<Grid item xs={6} sm={4} md={3} lg={2} key={logo.path}>
												<Card variant="outlined" sx={{ p: 1, position: "relative" }}>
													{removable && (
														<Tooltip title={t("BUTTON.DELETE", { defaultValue: "Delete" })}>
															<span>
																<IconButton
																	size="small"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleAssetDelete(logo.path, "logo");
																	}}
																	sx={{ position: "absolute", top: 4, right: 4 }}
																>
																	<DeleteOutlineIcon fontSize="small" />
																</IconButton>
															</span>
														</Tooltip>
													)}
													<Box
														sx={{
															width: "100%",
															height: 120,
															border: "1px solid #ddd",
															borderRadius: 1,
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															overflow: "hidden",
															background: "#fff",
														}}
													>
														<img
															src={`${ASSETS_BASE_URL}${logo.path}`}
															alt={logo.name}
															style={{ maxWidth: "100%", maxHeight: "100%" }}
														/>
													</Box>

													<Typography
														variant="body1"
														color="text.secondary"
														textAlign={"center"}
														mt={1}
														fontSize={12}
														noWrap
														title={logo.name}
													>
														{logo.name}
													</Typography>

													<Button
														fullWidth
														variant="outlined"
														sx={{ mt: 1 }}
														onClick={() => {
															setSelectedLogo(logo);
															setIconError("");
															setModalOpen(true);
														}}
													>
														{t("LABELS.SET_ACTIVE", { defaultValue: "Set Active" })}
													</Button>
												</Card>
											</Grid>
										);
									})}
								</Grid>
							)}
						</Grid>
					</Grid>
				</Box>

				{/* TAB 3 — BACKDROPS */}
				<Box hidden={activeTab !== 2}>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<Card variant="outlined">
								<CardContent>
									<Typography variant="h6" mb={1.5}>
										{t("LABELS.AUTH_BACKDROP", { defaultValue: "Auth Pages Backdrop" })}
									</Typography>
									<Box
										sx={{
											width: "100%",
											height: 180,
											borderRadius: 1,
											border: "1px solid #ddd",
											backgroundImage: backdropState.authBackdropPath
												? `url('${ASSETS_BASE_URL}${backdropState.authBackdropPath}')`
												: "none",
											backgroundSize: "cover",
											backgroundPosition: "center",
											backgroundColor: backdropState.authBackdropPath ? "transparent" : "#f0f0f0",
										}}
									/>
								</CardContent>
							</Card>
						</Grid>
						<Grid item xs={12} md={6}>
							<Card variant="outlined">
								<CardContent>
									<Typography variant="h6" mb={1.5}>
										{t("LABELS.HOME_BACKDROP", { defaultValue: "Home Page Backdrop" })}
									</Typography>
									<Box
										sx={{
											width: "100%",
											height: 180,
											borderRadius: 1,
											border: "1px solid #ddd",
											backgroundImage: backdropState.homeBackdropPath
												? `url('${ASSETS_BASE_URL}${backdropState.homeBackdropPath}')`
												: "none",
											backgroundSize: "cover",
											backgroundPosition: "center",
											backgroundColor: backdropState.homeBackdropPath ? "transparent" : "#f0f0f0",
										}}
									/>
								</CardContent>
							</Card>
						</Grid>

						<Grid item xs={12}>
							<Divider sx={{ my: 2 }} />
							<Box sx={{ mb: 2 }}>
								<Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
									{t("LABELS.BACKDROP_USAGE", { defaultValue: "Backdrop Usage" })}
								</Typography>
								<FormGroup>
									<FormControlLabel
										control={
											<Switch
												checked={!!backdropUsage.homeBackdropEnabled}
												onChange={(e) => updateBackdropUsage("homeBackdropEnabled", e.target.checked)}
											/>
										}
										label={t("LABELS.HOME_BACKDROP", { defaultValue: "Home Page Backdrop" })}
									/>
								</FormGroup>
								<Divider sx={{ my: 1 }} />
								<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
									{t("UI_TEXT.AUTH_BACKDROP_USAGE", { defaultValue: "Auth/Access pages using the Auth backdrop" })}
								</Typography>
								<FormGroup row>
									{AUTH_BACKDROP_USAGE_OPTIONS.map((item) => (
										<FormControlLabel
											key={item.key}
											control={
												<Switch
													checked={!!backdropUsage[item.key]}
													onChange={(e) => updateBackdropUsage(item.key, e.target.checked)}
												/>
											}
											label={item.label}
										/>
									))}
								</FormGroup>
							</Box>
							<Divider sx={{ mt: 2 }} />
							<Typography variant="h6" mt={2} mb={1}>
								{t("UI_TEXT.AVAILABLE_BACKDROPS", { defaultValue: "Available Backdrops" })}
							</Typography>

							<Box
								sx={{
									borderRadius: "10px",
									background: "#eee",
									p: 1.5,
									display: "flex",
									alignItems: "center",
									width: "100%",
									columnGap: 2,
									mb: 2,
								}}
							>
								<Button
									variant="contained"
									component="label"
									startIcon={<CloudUploadIcon />}
									disabled={backdropUploading}
								>
									{backdropUploading
										? t("BUTTON.UPLOADING", { defaultValue: "Uploading..." })
										: t("LABELS.UPLOAD_BACKDROP", { defaultValue: "Upload Backdrop" })}
									<input type="file" hidden accept="image/*" onChange={handleBackdropUpload} />
								</Button>
								<Typography variant="body1" fontSize="11px">
									{t("UI_TEXT.BACKDROP_UPLOAD_HELPER", {
										defaultValue: "Upload a JPG or PNG up to 5 MB for background use.",
									})}
								</Typography>
							</Box>
							{backdropLoading ? (
								<Grid container spacing={2}>
									{[...Array(6)].map((_, idx) => (
										<Grid item xs={6} sm={4} md={3} key={`backdrop-skel-${idx}`}>
											<Card variant="outlined" sx={{ p: 1 }}>
												<Skeleton
													variant="rectangular"
													sx={{ width: "100%", height: 150, borderRadius: 1 }}
												/>
												<Skeleton variant="text" sx={{ mt: 1 }} />
											</Card>
										</Grid>
									))}
								</Grid>
							) : (
								<Grid container spacing={2}>
									{backdropList.map((backdrop) => {
										const removable = backdrop.path?.startsWith("/branding/");
										return (
											<Grid item xs={6} sm={4} md={3} lg={2} key={backdrop.path}>
												<Card variant="outlined" sx={{ p: 1, position: "relative" }}>
													{removable && (
														<Tooltip title={t("BUTTON.DELETE", { defaultValue: "Delete" })}>
															<span>
																<IconButton
																	size="small"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleAssetDelete(backdrop.path, "backdrop");
																	}}
																	sx={{ position: "absolute", top: 4, right: 4 }}
																>
																	<DeleteOutlineIcon fontSize="small" />
																</IconButton>
															</span>
														</Tooltip>
													)}
													<Box
														sx={{
															width: "100%",
															height: 150,
															borderRadius: 1,
															border: "1px solid #ddd",
															backgroundImage: `url('${ASSETS_BASE_URL}${backdrop.path}')`,
															backgroundSize: "cover",
															backgroundPosition: "center",
														}}
													/>
													<Typography
														variant="body1"
														color="text.secondary"
														textAlign={"center"}
														mt={1}
														fontSize={12}
														noWrap
														title={backdrop.name}
													>
														{backdrop.name}
													</Typography>
													<Button
														fullWidth
														variant="outlined"
														sx={{ mt: 1 }}
														onClick={() => {
															setSelectedBackdrop(backdrop);
															setBackdropModalOpen(true);
														}}
													>
														{t("LABELS.SELECT_BACKDROP", { defaultValue: "Select Backdrop" })}
													</Button>
												</Card>
											</Grid>
										);
									})}
								</Grid>
							)}
						</Grid>
					</Grid>
				</Box>

				<Box hidden={activeTab != 3}>
					<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', rowGap: 2 }}>

						<Grid item xs={12} md={4}>
							<Card variant="outlined">
								<CardContent>
									<Typography variant="body1" mb={1.5}>
										{t("LABELS.ACTIVE_STOCK_IMAGE", { defaultValue: "Active Stock Image" })}
									</Typography>
									<Box
										sx={{
											width: "100%",
											height: 140,
											border: "1px solid #ddd",
											borderRadius: 1,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											overflow: "hidden",
											background: "#fff",
										}}
									>
										<img
											src={`${ASSETS_BASE_URL}${stockState.stockImagePath}`}
											alt="Stock"
											style={{ maxWidth: "100%", maxHeight: 120 }}
										/>
									</Box>
									<Button
										variant="outlined"
										fullWidth
										sx={{ mt: 2 }}
										onClick={() => setStockModalOpen(true)}
									>
										{t("LABELS.CHANGE_STOCK_IMAGE", { defaultValue: "Change Stock Image" })}
									</Button>
								</CardContent>
							</Card>
						</Grid>
						<Grid item xs={12} md={8}>
							<Divider sx={{ mb: 2 }} />
							<Typography variant="h6" mt={2} mb={1}>
								{t("LABELS.AVAILABLE_STOCK_IMAGES", { defaultValue: "Available Stock Images" })}
							</Typography>
							<Box
								sx={{
									borderRadius: "10px",
									background: "#eee",
									p: 1.5,
									display: "flex",
									alignItems: "center",
									width: "100%",
									columnGap: 2,
									mb: 2,
								}}
							>
								<Button
									variant="contained"
									component="label"
									startIcon={<CloudUploadIcon />}
									disabled={stockUploading}
								>
									{stockUploading
										? t("BUTTON.UPLOADING", { defaultValue: "Uploading..." })
										: t("LABELS.UPLOAD_STOCK_IMAGE", { defaultValue: "Upload Stock Image" })}
									<input type="file" hidden accept="image/*" onChange={handleStockUpload} />
								</Button>
								<Typography variant="body1" fontSize="11px">
									{t("LABELS.UPLOAD_A_JPG_OR_PNG_UP_TO_5_MB_FOR_THE_DEFAULT_STOCK_IMAGE", {
										defaultValue: "Upload a JPG or PNG up to 5 MB for the default stock image.",
									})}
								</Typography>
							</Box>
							{stockLoading ? (
								<Grid container spacing={2}>
									{[...Array(4)].map((_, i) => (
										<Grid item xs={6} sm={4} md={3} key={`stock-skel-${i}`}>
											<Card variant="outlined" sx={{ p: 1 }}>
												<Skeleton
													variant="rectangular"
													sx={{ width: "100%", height: 120, borderRadius: 1 }}
												/>
												<Skeleton variant="text" sx={{ mt: 1 }} />
											</Card>
										</Grid>
									))}
								</Grid>
							) : (
								<Grid container spacing={2}>
									{stockList.map((stock) => {
										const removable = stock.path?.startsWith("/branding/");
										return (
											<Grid item xs={6} sm={4} md={3} key={stock.path}>
												<Card variant="outlined" sx={{ p: 1, position: "relative" }}>
													{removable && (
														<Tooltip title={t("BUTTON.DELETE", { defaultValue: "Delete" })}>
															<span>
																<IconButton
																	size="small"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleAssetDelete(stock.path, "stock");
																	}}
																	sx={{ position: "absolute", top: 4, right: 4 }}
																>
																	<DeleteOutlineIcon fontSize="small" />
																</IconButton>
															</span>
														</Tooltip>
													)}
													<Box
														sx={{
															width: "100%",
															height: 120,
															border: "1px solid #ddd",
															borderRadius: 1,
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															overflow: "hidden",
															background: "#fff",
														}}
													>
														<img
															src={`${ASSETS_BASE_URL}${stock.path}`}
															alt={stock.name}
															style={{ maxWidth: "100%", maxHeight: "100%" }}
														/>
													</Box>
													<Typography
														variant="body1"
														color="text.secondary"
														textAlign={"center"}
														mt={1}
														fontSize={12}
														noWrap
														title={stock.name}
													>
														{stock.name}
													</Typography>
													<Button
														fullWidth
														variant="outlined"
														sx={{ mt: 1 }}
														onClick={() => persistStockSelection(stock.path)}
													>
														{t("LABELS.SET_ACTIVE", { defaultValue: "Set Active" })}
													</Button>
												</Card>
											</Grid>
										);
									})}
								</Grid>
							)}
						</Grid>
					</Box>
				</Box>


			</Box>

			{/* MODAL FOR SELECTING ROLE */}
			<ModalWrapper
				open={modalOpen}
				setOpen={setModalOpen}
				title={t("UI_TEXT.CHOOSE_LOGO_TYPE", { defaultValue: "Choose Logo Type" })}
				subtitle={t("UI_TEXT.CHOOSE_LOGO_USAGE", { defaultValue: "Apply this image as Primary, Sublogo or Favicon." })}
				content={
					selectedLogo && (
						<Box>
							{iconError && (
								<Alert severity="error" sx={{ mb: 2 }}>
									{iconError}
								</Alert>
							)}
							<Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} columnGap={1.5} width={"100%"}>
								<Box
									sx={{
										width: "100%",
										height: 140,
										border: "1px solid #ddd",
										borderRadius: 1,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										overflow: "hidden",
										background: "#fff",
									}}
								>

									<img
										src={`${ASSETS_BASE_URL}${selectedLogo.path}`}
										alt="selected"
										style={{ maxWidth: 200, maxHeight: 150 }}
									/>
								</Box>
								<Typography variant="body1" color="text.secondary" textAlign={"center"} mt={1} fontSize={12} >{selectedLogo.name}</Typography>
							</Box>




						</Box>
					)
				}
				actions={<>
					<Box display={"flex"} justifyContent={"center"} alignItems={"center"} columnGap={1.5} width={"100%"}>
						<Button variant="contained" onClick={() => persistLogoSelection("primary")}>
							{t("BUTTON.SET_PRIMARY_LOGO", { defaultValue: "Set as Primary Logo" })}
						</Button>

						<Button variant="contained" onClick={() => persistLogoSelection("footerPrimary")}>
							{t("BUTTON.SET_FOOTER_PRIMARY_LOGO", { defaultValue: "Set as Footer Primary Logo" })}
						</Button>

						<Button variant="contained" onClick={() => persistLogoSelection("secondary")}>
							{t("BUTTON.SET_FOOTER_LOGO", { defaultValue: "Set as Footer / Sublogo" })}
						</Button>

						<Button variant="contained" onClick={() => persistLogoSelection("icon")}>
							{t("BUTTON.SET_ICON", { defaultValue: "Set as Browser Icon (Favicon)" })}
						</Button>
					</Box>
				</>}
			/>
			<ModalWrapper
				open={stockModalOpen}
				setOpen={setStockModalOpen}
				title={t("LABELS.STOCK_IMAGE", { defaultValue: "Stock Image" })}
				subtitle={t("UI_TEXT.STOCK_IMAGE_MODAL_SUBTITLE", { defaultValue: "Choose an image to use as the default stock fallback." })}
				content={
					<Box>
						{stockList.length === 0 ? (
							<Typography variant="body2" color="text.secondary">
								{t("UI_TEXT.NO_STOCK_IMAGES", { defaultValue: "No stock images available." })}
							</Typography>
						) : (
							<Grid container spacing={2}>
								{stockList.map((stock) => (
									<Grid item xs={6} sm={4} md={3} key={`stock-modal-${stock.path}`}>
										<Card variant="outlined" sx={{ p: 1 }}>
											<Box
												sx={{
													width: "100%",
													height: 120,
													border: "1px solid #ddd",
													borderRadius: 1,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													overflow: "hidden",
													background: "#fff",
												}}
											>
												<img
													src={`${ASSETS_BASE_URL}${stock.path}`}
													alt={stock.name}
													style={{ maxWidth: "100%", maxHeight: "100%" }}
												/>
											</Box>
											<Button
												fullWidth
												variant="outlined"
												sx={{ mt: 1 }}
												onClick={() => persistStockSelection(stock.path)}
											>
												{t("LABELS.SET_ACTIVE", { defaultValue: "Set Active" })}
											</Button>
										</Card>
									</Grid>
								))}
							</Grid>
						)}
					</Box>
				}
			/>
			<ModalWrapper
				open={backdropModalOpen}
				setOpen={setBackdropModalOpen}
				title={t("LABELS.CHOOSE_BACKDROP_TYPE", { defaultValue: "Choose Backdrop Type" })}
				subtitle={t("LABELS.SET_THIS_IMAGE_AS_THE_AUTH_PAGES_OR_HOME_PAGE_BACKDROP", {
					defaultValue: "Set as Auth Pages or Home Page background.",
				})}
				content={
					selectedBackdrop && (
						<Box>
							<Box
								sx={{
									width: "100%",
									height: 180,
									borderRadius: 1,
									border: "1px solid #ddd",
									backgroundImage: `url('${ASSETS_BASE_URL}${selectedBackdrop.path}')`,
									backgroundSize: "cover",
									backgroundPosition: "center",
								}}
							/>
							<Typography
								variant="body1"
								color="text.secondary"
								textAlign={"center"}
								mt={1}
								fontSize={12}
							>
								{selectedBackdrop.name}
							</Typography>
						</Box>
					)
				}
				actions={
					<Box display="flex" justifyContent="center" alignItems="center" columnGap={1.5} width="100%">
						<Button variant="contained" onClick={() => persistBackdropSelection("auth")}>
							{t("LABELS.SET_FOR_AUTH_PAGES", { defaultValue: "Set for Auth Pages" })}
						</Button>
						<Button variant="contained" onClick={() => persistBackdropSelection("home")}>
							{t("LABELS.SET_FOR_HOME_BACKDROP", { defaultValue: "Set for Home Page" })}
						</Button>
						<Button variant="outlined" onClick={() => persistBackdropSelection("auth", "")}>
							{t("LABELS.CLEAR_AUTH_BACKDROP", { defaultValue: "Clear Auth Backdrop" })}
						</Button>
						<Button variant="outlined" onClick={() => persistBackdropSelection("home", "")}>
							{t("LABELS.CLEAR_HOME_BACKDROP", { defaultValue: "Clear Home Backdrop" })}
						</Button>
					</Box>
				}
			/>
		</>
	);
};

export default CompanyBranding;
